import type { ReactNode } from 'react';
import type {
  CharityClaimWire,
  CharityVerificationClaimantDetail,
  CharityVerificationContactConfirmation,
  CharityVerificationDetail,
  CharityVerificationOrganization,
  CharityVerificationPriorDecision,
  CharityVerificationQueueItem,
  CharityDomainRegistration,
  ProfileFieldsWire,
} from '../api/adminOps';
import OrganizationProfileDiff from '../components/OrganizationProfileDiff';
import { StatusChip } from '../components/ui';
import {
  claimantLabel,
  contactStateChip,
  prettyJson,
  statusChip,
} from './charityVerification.logic';
import { decisionLabel, REJECTION_REASON_OPTIONS } from './charityVerificationDecision.logic';
import { formatWhen } from './userDeletion.logic';

export const SECTION_HEADING =
  'text-xs font-semibold uppercase tracking-wider text-gray-text/70 mb-2';

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-text">{label}</dt>
      <dd className="text-sm text-white break-words">{children}</dd>
    </div>
  );
}

export function Empty() {
  return <span className="text-gray-text">—</span>;
}

export function TextValue({ value }: { value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') return <Empty />;
  return <>{String(value)}</>;
}

function YesNo({ value }: { value: boolean }) {
  return <>{value ? 'Yes' : 'No'}</>;
}

function Flags({ flags }: { flags: readonly string[] }) {
  if (flags.length === 0) return <Empty />;
  return (
    <span className="flex flex-wrap gap-1">
      {flags.map(flag => (
        <StatusChip key={flag} label={flag} tone="warn" />
      ))}
    </span>
  );
}

function rejectionReasonLabel(code: string): string {
  return REJECTION_REASON_OPTIONS.find(option => option.value === code)?.label ?? code;
}

function ClaimSummary({
  claim,
  row,
}: {
  claim: CharityClaimWire;
  row: CharityVerificationQueueItem | null;
}) {
  const status = statusChip(claim.status);
  return (
    <section aria-labelledby="charity-claim-summary" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="charity-claim-summary" className="sr-only">
          Claim
        </h3>
        <StatusChip label={status.label} tone={status.tone} />
        {claim.disputed && <StatusChip label="Disputed" tone="crit" />}
        {row?.second_operator_required && (
          <StatusChip label="Second operator required" tone="info" />
        )}
        {row?.sampled_for_review && <StatusChip label="Sampled for review" tone="neutral" />}
        {claim.verified_by_automation && <StatusChip label="Verified by automation" tone="ok" />}
      </div>
      <p className="text-xs text-gray-text">{status.hint}</p>
      {claim.approval_pending_by !== null && (
        <p
          role="status"
          className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-sm text-yellow-300"
        >
          First approval recorded by{' '}
          <span className="font-mono text-xs">{claim.approval_pending_by}</span>{' '}
          {formatWhen(claim.approval_pending_at)}. A different operator must record the second.
        </p>
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Field label="Claim id">
          <span className="font-mono text-xs">{claim.id}</span>
        </Field>
        <Field label="Created">{formatWhen(claim.created_at)}</Field>
        <Field label="Submitted">{formatWhen(claim.submitted_at)}</Field>
        <Field label="Decided">{formatWhen(claim.decided_at)}</Field>
        <Field label="Verified">{formatWhen(claim.verified_at)}</Field>
        <Field label="Revoked">{formatWhen(claim.revoked_at)}</Field>
        <Field label="Expires">{formatWhen(claim.expires_at)}</Field>
        <Field label="Resubmit deadline">{formatWhen(claim.resubmit_deadline_at)}</Field>
        <Field label="Rejection reason">
          {claim.rejection_reason_code === null ? (
            <Empty />
          ) : (
            rejectionReasonLabel(claim.rejection_reason_code)
          )}
        </Field>
        <Field label="Resubmit allowed">
          <YesNo value={claim.resubmit_allowed} />
        </Field>
        {claim.rejection_note !== null && (
          <div className="col-span-2">
            <Field label="Rejection note">
              <span className="whitespace-pre-wrap">{claim.rejection_note}</span>
            </Field>
          </div>
        )}
        <div className="col-span-2">
          <Field label="Eligibility flags">
            <Flags flags={claim.eligibility_flags} />
          </Field>
        </div>
      </dl>
    </section>
  );
}

function OrganizationPanel({ organization }: { organization: CharityVerificationOrganization }) {
  const address = [
    organization.address,
    organization.city,
    organization.state,
    organization.postal_code,
  ]
    .filter(part => part !== null && part !== '')
    .join(', ');
  return (
    <section aria-labelledby="charity-organization">
      <h3 id="charity-organization" className={SECTION_HEADING}>
        Organization (IRS Business Master File)
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <div className="col-span-2">
          <Field label="Name">
            <span className="font-medium">{organization.name}</span>
          </Field>
        </div>
        <Field label="Legal name">{organization.legal_name}</Field>
        <Field label="Alternate name">
          <TextValue value={organization.alternate_name} />
        </Field>
        <Field label="EIN">
          <span className="font-mono text-xs">{organization.ein}</span>
        </Field>
        <Field label="Handle">
          <TextValue value={organization.handle} />
        </Field>
        <div className="col-span-2">
          <Field label="Address">
            <TextValue value={address} />
          </Field>
        </div>
        <Field label="Ruling date">
          <TextValue value={organization.ruling_date} />
        </Field>
        <Field label="Subsection">
          <TextValue value={organization.subsection} />
        </Field>
        <Field label="Foundation code">
          <TextValue value={organization.foundation_code} />
        </Field>
        <Field label="Foundation class">
          <TextValue value={organization.foundation_class} />
        </Field>
        <Field label="Deductibility">
          <TextValue value={organization.deductibility} />
        </Field>
        <Field label="Status code">
          <TextValue value={organization.status_code} />
        </Field>
        <Field label="Affiliation code">
          <TextValue value={organization.affiliation_code} />
        </Field>
        <Field label="Group exemption">
          <TextValue value={organization.group_exemption_number} />
        </Field>
        <Field label="BMF website">
          <TextValue value={organization.website_url} />
        </Field>
        <Field label="Organization id">
          <span className="font-mono text-xs">{organization.id}</span>
        </Field>
        <div className="col-span-2">
          <Field label="Eligibility flags">
            <Flags flags={organization.eligibility_flags} />
          </Field>
        </div>
        <Field label="Publication 78">
          {organization.pub78 === null ? (
            'Not checked'
          ) : (
            <>
              {organization.pub78.listed ? 'Listed' : 'Not listed'}
              {organization.pub78.deductibility_codes.length > 0 &&
                ` · codes ${organization.pub78.deductibility_codes.join(', ')}`}
              {organization.pub78.list_version_date !== null &&
                ` · list of ${organization.pub78.list_version_date}`}
            </>
          )}
        </Field>
        <Field label="Auto-revocation">
          {organization.revocation === null ? (
            'None on record'
          ) : (
            <>
              Revoked {organization.revocation.revocation_date ?? 'on an unknown date'}
              {organization.revocation.reinstatement_date !== null &&
                ` · reinstated ${organization.revocation.reinstatement_date}`}
            </>
          )}
        </Field>
      </dl>
      {organization.filings.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-gray-text" aria-label="Form 990 filings">
          {organization.filings.map(filing => (
            <li key={`${filing.return_type}-${filing.tax_period}`}>
              <span className="text-white">{filing.return_type}</span> · {filing.tax_period}
              {filing.website_domain !== null && ` · ${filing.website_domain}`}
              {filing.filing_date !== null && ` · filed ${filing.filing_date}`}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ClaimantPanel({
  claimant,
  organizationName,
}: {
  claimant: CharityVerificationClaimantDetail;
  organizationName: string;
}) {
  return (
    <section aria-labelledby="charity-claimant">
      <h3 id="charity-claimant" className={SECTION_HEADING}>
        Claimant
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Field label="Name">{claimantLabel(claimant.display_name, organizationName)}</Field>
        <Field label="Account created">{formatWhen(claimant.created_at)}</Field>
        <Field label="Email">{claimant.email}</Field>
        <Field label="Email domain">{claimant.email_domain}</Field>
        <Field label="User id">
          <span className="font-mono text-xs">{claimant.user_id}</span>
        </Field>
        <Field label="AI processing consent">
          {claimant.ai_consent.status}
          {claimant.ai_consent.providers_version !== null &&
            ` · providers ${claimant.ai_consent.providers_version}`}
        </Field>
      </dl>
    </section>
  );
}

function WebsitePanel({
  domain,
  registration,
}: {
  domain: string | null;
  registration: CharityDomainRegistration | null;
}) {
  return (
    <section aria-labelledby="charity-website">
      <h3 id="charity-website" className={SECTION_HEADING}>
        Website domain
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Field label="Domain">
          <TextValue value={domain} />
        </Field>
        <Field label="RDAP status">
          <TextValue value={registration?.rdap_status ?? null} />
        </Field>
        <Field label="Registered">{formatWhen(registration?.registered_at, 'Unknown')}</Field>
        <Field label="Registrar">
          <TextValue value={registration?.registrar ?? null} />
        </Field>
        <Field label="Looked up">{formatWhen(registration?.fetched_at, 'Not looked up')}</Field>
      </dl>
    </section>
  );
}

function ContactPanel({ contact }: { contact: CharityVerificationContactConfirmation }) {
  const chip = contactStateChip(contact.state);
  return (
    <section aria-labelledby="charity-contact">
      <h3 id="charity-contact" className={SECTION_HEADING}>
        Contact confirmation
      </h3>
      <div className="mb-2">
        <StatusChip label={chip.label} tone={chip.tone} />
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Field label="Method">
          <TextValue value={contact.method} />
        </Field>
        <Field label="Sent to">
          <TextValue value={contact.sent_to_masked} />
        </Field>
        <Field label="Sent">{formatWhen(contact.sent_at)}</Field>
        <Field label="Code expires">{formatWhen(contact.expires_at)}</Field>
        <Field label="Attempts">{contact.attempts}</Field>
        <Field label="Resends">{contact.resend_count}</Field>
        <Field label="Locked">{formatWhen(contact.locked_at, 'Not locked')}</Field>
        <Field label="Confirmed">{formatWhen(contact.confirmed_at, 'Not confirmed')}</Field>
      </dl>
    </section>
  );
}

function ProfilePanel({
  live,
  draft,
}: {
  live: ProfileFieldsWire;
  draft: ProfileFieldsWire | null;
}) {
  return (
    <section aria-labelledby="charity-profile">
      <h3 id="charity-profile" className={SECTION_HEADING}>
        Profile — live versus draft
      </h3>
      <OrganizationProfileDiff live={live} draft={draft} />
    </section>
  );
}

function PriorDecisions({ decisions }: { decisions: CharityVerificationPriorDecision[] }) {
  return (
    <section aria-labelledby="charity-prior-decisions">
      <h3 id="charity-prior-decisions" className={SECTION_HEADING}>
        Previous decisions
      </h3>
      {decisions.length === 0 ? (
        <p className="text-sm text-gray-text">No decision has been recorded on this claim.</p>
      ) : (
        <ul className="space-y-2 text-xs">
          {decisions.map(decision => (
            <li key={`${decision.decided_at}-${decision.decided_by}`}>
              <span className="font-medium text-white">{decisionLabel(decision.decision)}</span>{' '}
              <span className="text-gray-text">
                · {formatWhen(decision.decided_at)} · by{' '}
                <span className="font-mono">{decision.decided_by}</span>
                {decision.reason_code !== null &&
                  ` · ${rejectionReasonLabel(decision.reason_code)}`}
              </span>
              {decision.note !== null && (
                <p className="text-gray-text whitespace-pre-wrap">{decision.note}</p>
              )}
              {decision.teos_url !== null && (
                <p className="text-gray-text break-all">TEOS: {decision.teos_url}</p>
              )}
              {decision.rubric !== null && decision.rubric !== undefined && (
                <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-dark-lighter p-2 font-mono text-gray-text">
                  {prettyJson(decision.rubric)}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function AllowedActions({ actions }: { actions: readonly string[] }) {
  return (
    <section aria-labelledby="charity-allowed-actions">
      <h3 id="charity-allowed-actions" className={SECTION_HEADING}>
        Allowed actions
      </h3>
      {actions.length === 0 ? (
        <p className="text-sm text-gray-text">No decision is allowed in this status.</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {actions.map(action => (
            <StatusChip key={action} label={decisionLabel(action)} tone="info" />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * DM-7976: the claim, the organization's IRS record, the claimant and the
 * confirmation state — everything a reviewer reads before the evidence
 * (documents, extraction, automation) in CharityVerificationEvidence.
 */
export default function CharityVerificationCaseView({
  detail,
  row,
}: {
  detail: CharityVerificationDetail;
  row: CharityVerificationQueueItem | null;
}) {
  return (
    <div className="space-y-5">
      <ClaimSummary claim={detail.claim} row={row} />
      <OrganizationPanel organization={detail.organization} />
      <ClaimantPanel
        claimant={detail.claimant}
        organizationName={detail.organization.legal_name || detail.organization.name}
      />
      <WebsitePanel domain={detail.website_domain} registration={detail.domain_registration} />
      <ContactPanel contact={detail.contact_confirmation} />
      <ProfilePanel live={detail.profile_live} draft={detail.profile_draft} />
      <PriorDecisions decisions={detail.decisions} />
      <AllowedActions actions={detail.allowed_actions} />
    </div>
  );
}
